import { type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, Check, Copy, Infinity, LogOut, Share, User as UserIcon, Menu as MenuIcon, Search, Lightbulb } from 'lucide-react';

// Lucide v1+ no longer ships brand icons. Inline the LinkedIn mark.
function Linkedin(props: React.SVGProps<SVGSVGElement> & { size?: number }) {
  const { size, width, height, ...rest } = props;
  const w = width ?? size ?? 16;
  const h = height ?? size ?? 16;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      width={w}
      height={h}
      {...rest}
    >
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}
import type { PreviewMeta } from '@ah/shared/preview';
import { ShareProvider, useCurrentSharePath } from './ShareContext';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './index';
import { cn } from './lib/utils';

export interface AppShellUser {
  id: string;
  name: string;
  email: string;
  pictureUrl?: string | null;
  isAdmin?: boolean;
  linkedinUrl?: string | null;
}

export interface SidebarLink {
  to: string;
  label: string;
  icon: ReactNode;
}

export interface SidebarGroup {
  /** Small uppercase caption shown above the group's items. */
  label: string;
  items: SidebarLink[];
}

export type SidebarEntry = SidebarLink | SidebarGroup;

function isGroup(e: SidebarEntry): e is SidebarGroup {
  return 'items' in e;
}

export interface OtherAccount {
  id: string;
  name: string;
  email: string;
  pictureUrl?: string | null;
  linkedinUrl?: string | null;
}

export interface AppShellProps {
  user: AppShellUser;
  /** Flat links or groups of links. Groups render with a caption header. */
  links: SidebarEntry[];
  children: ReactNode;
  banner?: ReactNode;
  onLogout: () => void;
  brandHref?: string;
  /** Optional custom brand mark to render in place of the default infinity glyph. */
  brandIcon?: ReactNode;
  /** Other logged-in accounts for the account switcher. */
  otherAccounts?: OtherAccount[];
  /** Called when the user picks a different account. */
  onSwitchAccount?: (id: string) => void;
  /** Called when the user clicks the feedback button in the top bar. */
  onFeedback?: () => void;
  /** Extra action buttons rendered in the top bar, before the feedback button. */
  topBarActions?: ReactNode;
  /**
   * Base URL of the API (e.g. https://api.underlings.com). When provided,
   * a share button appears in the top bar that calls GET <apiBaseUrl>/preview?path=<canonical>
   * and shows a share modal.
   */
  apiBaseUrl?: string;
  /**
   * Host of the public share-landing surface (e.g. https://share.underlings.com).
   * When the current canonical path starts with one of `sharePathPrefixes`, the
   * share-link copy field uses `<shareBaseUrl><path>` instead of the SPA origin
   * so the pasted link points at the share host directly. Falls back to
   * `window.location.origin` for paths without a share landing.
   */
  shareBaseUrl?: string;
  /**
   * Path prefixes (e.g. `['/asks/', '/fairy/']`) that have a public share
   * landing on `shareBaseUrl`. Only applied when `shareBaseUrl` is also set.
   */
  sharePathPrefixes?: string[];
  /**
   * Custom notifications dropdown rendered in place of the default bell.
   * When omitted, a static placeholder dropdown is shown so existing
   * callers keep working.
   */
  notificationsSlot?: ReactNode;
  /** Content rendered at the bottom of the app rail (e.g. a "create" button). */
  railFooter?: ReactNode;
}

export function AppShell({ user, links, children, banner, onLogout, brandHref = '/', brandIcon, otherAccounts, onSwitchAccount, onFeedback, topBarActions, notificationsSlot, apiBaseUrl, shareBaseUrl, sharePathPrefixes, railFooter }: AppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMeta, setShareMeta] = useState<PreviewMeta | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const canonicalPath = useCurrentSharePath();

  // Flatten sidebar links for the command palette
  const flatLinks = links.flatMap((entry) => (isGroup(entry) ? entry.items : [entry]));

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCmdkOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  async function handleShare() {
    if (!apiBaseUrl) return;
    const path = canonicalPath ?? location.pathname;
    setShareMeta(null);
    setShareOpen(true);
    setShareLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/preview?path=${encodeURIComponent(path)}`);
      if (res.ok) setShareMeta(await res.json() as PreviewMeta);
    } catch { /* show modal with just the URL */ }
    setShareLoading(false);
  }

  const shareUrl = (() => {
    if (typeof window === 'undefined') return '';
    const path = canonicalPath ?? location.pathname;
    if (shareBaseUrl && sharePathPrefixes?.some((p) => path.startsWith(p))) {
      return `${shareBaseUrl.replace(/\/$/, '')}${path}`;
    }
    return `${window.location.origin}${path}`;
  })();

  async function handleCopy() {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <ShareProvider>
    <div className="h-dvh flex flex-col overflow-hidden bg-background text-foreground">
      {/* Topbar — full width */}
      <header className="sticky top-0 z-30 h-14 border-b bg-background/95 backdrop-blur flex items-center gap-3 pr-4">
        {/* Brand — same width as the rail so the icon sits centred above it */}
        <div className="hidden md:flex items-center justify-center w-14 shrink-0">
          <Link to={brandHref} className="flex items-center justify-center">
            {brandIcon ?? <Infinity size={28} className="text-primary" strokeWidth={1.75} />}
          </Link>
        </div>
        <button
          className="md:hidden p-2 ml-2 rounded hover:bg-muted"
          aria-label="Toggle navigation"
          onClick={() => setMobileOpen((v) => !v)}
        >
          <MenuIcon size={18} />
        </button>
        <Link to={brandHref} className="md:hidden flex items-center gap-2 min-w-0">
          {brandIcon ?? <Infinity size={28} className="text-primary" strokeWidth={1.75} />}
        </Link>
        <button
          onClick={() => setCmdkOpen(true)}
          className="hidden sm:flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted transition w-72"
        >
          <Search size={14} />
          <span>Search…</span>
          <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-0.5 rounded border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            ⌘K
          </kbd>
        </button>
        <div className="flex-1" />
        {apiBaseUrl && (
          <Button variant="ghost" size="icon" className="size-8" onClick={handleShare} aria-label="Share">
            <Share size={16} />
          </Button>
        )}
        {onFeedback && (
          <Button variant="ghost" size="icon" className="size-8" onClick={onFeedback} aria-label="Feedback">
            <Lightbulb size={16} />
          </Button>
        )}
        {topBarActions}
        {notificationsSlot ?? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8" aria-label="Notifications">
                <Bell size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-0">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="font-semibold text-sm text-foreground">Notifications</span>
              </div>
              {/* Empty state */}
              <div className="py-8 flex flex-col items-center gap-2 text-center px-4">
                <Bell className="w-8 h-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8 rounded-full">
              <Avatar className="size-7">
                {user.pictureUrl ? <AvatarImage src={user.pictureUrl} alt={user.name} /> : null}
                <AvatarFallback>{initials(user.name)}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            {/* Current account */}
            <div className="px-2 py-1.5 flex items-center gap-2">
              <Avatar className="size-6 shrink-0">
                {user.pictureUrl ? <AvatarImage src={user.pictureUrl} alt={user.name} /> : null}
                <AvatarFallback className="text-[10px]">{initials(user.name)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user.name}</div>
                <AccountSubline
                  email={user.email}
                  linkedinUrl={user.linkedinUrl}
                />
              </div>
              <Check size={14} className="text-primary shrink-0" />
            </div>

            {/* Other accounts */}
            {otherAccounts && otherAccounts.length > 0 && (
              <>
                <DropdownMenuSeparator />
                {otherAccounts.map((acct) => (
                  <DropdownMenuItem
                    key={acct.id}
                    onClick={() => onSwitchAccount?.(acct.id)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Avatar className="size-6 shrink-0">
                      {acct.pictureUrl ? <AvatarImage src={acct.pictureUrl} alt={acct.name} /> : null}
                      <AvatarFallback className="text-[10px]">{initials(acct.name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">{acct.name}</div>
                      <AccountSubline
                        email={acct.email}
                        linkedinUrl={acct.linkedinUrl}
                      />
                    </div>
                  </DropdownMenuItem>
                ))}
              </>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                <UserIcon size={14} /> Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-destructive focus:text-destructive">
              <LogOut size={14} /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {banner}

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* App rail */}
        <TooltipProvider delayDuration={0}>
          <aside
            className={cn(
              'border-r bg-muted/30 w-14 shrink-0 flex-col items-center py-3 md:flex',
              mobileOpen ? 'flex absolute inset-y-14 left-0 z-20 bg-background w-14' : 'hidden',
            )}
          >
            <div className="flex-1 min-h-0 w-full flex flex-col items-center gap-1 overflow-y-auto">
              {links.map((entry, i) => {
                if (isGroup(entry)) {
                  return (
                    <div key={`g-${i}`} className="flex flex-col items-center gap-1">
                      {entry.items.map((l) => (
                        <RailItem key={l.to} link={l} onNavigate={() => setMobileOpen(false)} />
                      ))}
                    </div>
                  );
                }
                return (
                  <RailItem key={entry.to} link={entry} onNavigate={() => setMobileOpen(false)} />
                );
              })}
            </div>
            {railFooter ? (
              <div className="shrink-0 w-full flex flex-col items-center pt-2 border-t mt-2">
                {railFooter}
              </div>
            ) : null}
          </aside>
        </TooltipProvider>

        <main className="flex-1 min-w-0 overflow-y-auto">{children}</main>
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share</DialogTitle>
          </DialogHeader>
          {shareLoading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-4 w-3/4 bg-muted rounded" />
              <div className="h-3 w-full bg-muted rounded" />
              <div className="h-3 w-5/6 bg-muted rounded" />
            </div>
          ) : (
            <div className="space-y-4">
              {shareMeta?.imageUrl && (
                <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                  <img src={shareMeta.imageUrl} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              {shareMeta && (
                <div className="space-y-1">
                  <p className="font-semibold text-sm leading-snug">{shareMeta.title}</p>
                  {shareMeta.description && (
                    <p className="text-xs text-muted-foreground line-clamp-3">{shareMeta.description}</p>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  className="flex-1 rounded-md border bg-muted/40 px-3 py-1.5 text-xs font-mono text-muted-foreground truncate outline-none"
                  onFocus={(e) => e.target.select()}
                />
                <Button size="sm" variant="outline" onClick={handleCopy} className="shrink-0 gap-1.5">
                  <Copy size={13} />
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CommandDialog open={cmdkOpen} onOpenChange={setCmdkOpen}>
        <CommandInput placeholder="Where do you want to go?" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Pages">
            {flatLinks.map((link) => (
              <CommandItem
                key={link.to}
                onSelect={() => {
                  navigate(link.to);
                  setCmdkOpen(false);
                }}
                className="gap-2"
              >
                {link.icon}
                <span>{link.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </div>
    </ShareProvider>
  );
}

function RailItem({ link, onNavigate }: { link: SidebarLink; onNavigate: () => void }) {
  const location = useLocation();
  const active =
    location.pathname === link.to ||
    (link.to !== '/' && location.pathname.startsWith(link.to + '/'));
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={link.to}
          onClick={onNavigate}
          className={cn(
            'flex items-center justify-center size-10 rounded-md transition',
            active
              ? 'bg-foreground/10'
              : 'hover:bg-foreground/5 text-muted-foreground',
          )}
        >
          {link.icon}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {link.label}
      </TooltipContent>
    </Tooltip>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase() || '?';
}

/** Extract the `<username>` handle from a LinkedIn profile URL. */
function linkedinHandle(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/\/in\/([^/?#]+)/);
  return m?.[1] ?? null;
}

function AccountSubline({
  email,
  linkedinUrl,
}: {
  email: string;
  linkedinUrl?: string | null;
}) {
  const handle = linkedinHandle(linkedinUrl);
  const label = handle ?? email;
  const iconPx = 11;
  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground min-w-0">
      <Linkedin
        size={iconPx}
        width={iconPx}
        height={iconPx}
        style={{ width: iconPx, height: iconPx }}
        className="shrink-0"
      />
      <span className="truncate">{label}</span>
    </div>
  );
}
